// Pure helpers for the heatmap axis-header tooltips. Kept framework-free so the
// score-spread math and the header → RichTooltip payload mapping can be unit
// tested directly, and so MissionImpactHeatmap stays focused on interaction.

// Compute the score spread for a single header (one row or one column) from the
// heatmap's own cells. Returns null when the header has no scored cells.
//
// axis 'column' → spread of a column across the rows it intersects (cells are
//   matched by dimensionKey; each cell is labeled by its row/portfolioLabel).
// axis 'row'    → spread of a row across the columns it intersects (cells are
//   matched by portfolioId; each cell is labeled by its column/dimensionLabel).
//
// `strongThreshold` defaults to 80 (the market-research "strong" band). Callers
// that aren't band-scored simply ignore strongCount.
export function buildHeatmapHeaderSpread(cells, axis, key, { strongThreshold = 80 } = {}) {
  const matchField = axis === 'column' ? 'dimensionKey' : 'portfolioId';
  const labelField = axis === 'column' ? 'portfolioLabel' : 'dimensionLabel';
  const relevant = (cells || []).filter(
    (cell) => cell && cell[matchField] === key && cell.score != null,
  );
  if (!relevant.length) return null;

  let best = relevant[0];
  let weakest = relevant[0];
  let strongCount = 0;
  for (const cell of relevant) {
    if (cell.score > best.score) best = cell;
    if (cell.score < weakest.score) weakest = cell;
    if (cell.score >= strongThreshold) strongCount += 1;
  }
  return {
    best: { label: best[labelField], score: best.score },
    weakest: { label: weakest[labelField], score: weakest.score },
    strongCount,
    total: relevant.length,
  };
}

// Map an axis header + its (optional) authored metadata and computed spread into
// a RichTooltipCard payload. Summary/trust/formula are left null when there's no
// content so RichTooltipCard omits those sections. The "Strong N/total" line is
// only included when a scaleNote is present (i.e. the surface is band-scored,
// like market research) — derived-only surfaces show Best/Weakest only.
export function normalizeHeaderToRichTooltip({
  axis,
  key,
  label,
  fullLabel,
  whatIs = null,
  scaleNote = null,
  spread = null,
  trust = null,
  tone = 'slate',
}) {
  const includeStrongCount = Boolean(scaleNote);
  const drivers = [];
  if (spread) {
    drivers.push({ label: 'Best fit', valueLabel: `${spread.best.label} · ${spread.best.score}` });
    drivers.push({
      label: 'Weakest',
      valueLabel: `${spread.weakest.label} · ${spread.weakest.score}`,
    });
    if (includeStrongCount) {
      drivers.push({ label: 'Strong fits', valueLabel: `${spread.strongCount} / ${spread.total}` });
    }
  }

  return {
    surfaceId: `${axis}:${key}`,
    variant: 'standard',
    interactionMode: 'pinnable',
    entityLabel: fullLabel || label,
    sliceLabel: null,
    headlineValue: {
      primary: spread ? `Best ${spread.best.score}` : '—',
      secondary: spread ? `Low ${spread.weakest.score}` : null,
      tone,
    },
    summary: whatIs || null,
    driversLabel: 'Score spread',
    drivers,
    formula: scaleNote
      ? { label: 'How it’s measured', ruleLabel: scaleNote, weightedInputs: [] }
      : null,
    trust: trust || null,
    limitations: [],
    drillTarget: null,
  };
}
